import Vue from 'vue';
import { AccessTokenStatus, MsalResult } from '@/enums';
import { GraphApiUtils } from './graphApiUtils';
import { AuthResult } from '@/models/authResult';
import { AuthUtils } from './authUtils';
import { Consts } from '@/consts';
import { StoreUtils } from './storeUtils';
import { AccessTokenInfo, IAccessTokenInfo } from '@/models/accessTokenInfo';

export class AppUtils {
	/**
	 * アクセストークンが有効かどうかチェックし、無効な場合は再取得を試みます。
	 * ※このメソッドはVueのグローバル変数にアクセスします。
	 * @param tryReacquire 再取得を試みるかどうか
	 */
	public static async checkAccessToken(tryReacquire: boolean = true): Promise<boolean> {
		try {
			const accessTokenStatus: AccessTokenStatus = await GraphApiUtils.getAccessTokenStatus(Vue.$accessTokenInfo);
			switch (accessTokenStatus) {
				case AccessTokenStatus.Valid:
					// 有効であれば何もしない。
					return true;
				case AccessTokenStatus.EmptyOrUndefined:
				case AccessTokenStatus.Expired:
				case AccessTokenStatus.InvalidAuthenticationToken:
				case AccessTokenStatus.AuthenticationError:
					if (!tryReacquire) {
						// いずれかステータスの場合で再取得を試みない場合はアクセストークンを削除する。
						AppUtils.removeAccessTokenInfo();
						return false;
					}
					// ここでリダイレクトを伴わないアクセストークンの取得を試みる。
					// 成功した場合はローカルストレージおよびグローバルのアクセストークンを更新する。
					// この結果を受けて呼び出し元でログイン画面へ誘導する必要がある。
					const authResult: AuthResult = await AuthUtils.acquireTokenSilent(Vue.$userAgentApplication);
					if (authResult.result !== MsalResult.Success) {
						return false;
					}
					AppUtils.setAccessTokenInfo(authResult.accessTokenInfo);
					return true;
				case AccessTokenStatus.Unknown:
				case AccessTokenStatus.FetchError:
				case AccessTokenStatus.ParseError:
				default:
					// いずれかステータスの場合、アクセストークンを削除する。
					AppUtils.removeAccessTokenInfo();
					console.error('checkTokenError: ' + accessTokenStatus);
					return false;
			}
		} catch (e) {
			AppUtils.removeAccessTokenInfo();
			console.error('checkTokenError: unknown');
			return false;
		}
	}

	/**
	 * アクセストークン情報をグローバル変数およびローカルストレージにセットする。
	 * @param accessTokenInfo
	 */
	public static setAccessTokenInfo(accessTokenInfo: AccessTokenInfo): void {
		try {
			if (accessTokenInfo === undefined) {
				return;
			}
			Vue.$accessTokenInfo = accessTokenInfo;
			StoreUtils.setLocalStorage(Consts.LOCAL_STORAGE_KEY_MSAL_ACCESSTOKENINFO, accessTokenInfo);
		} catch (e) {
			console.error(e);
		}
	}

	/**
	 * アクセストークン情報をローカルストレージからグローバル変数にセットする。
	 */
	public static loadAccessTokenInfo(): AccessTokenInfo {
		try {
			const iAccessTokenInfo: IAccessTokenInfo | null = StoreUtils.getLocalStorageAndJsonParse<IAccessTokenInfo>(
				Consts.LOCAL_STORAGE_KEY_MSAL_ACCESSTOKENINFO);
			let accessTokenInfo: AccessTokenInfo = new AccessTokenInfo();
			if (iAccessTokenInfo === null) {
				accessTokenInfo.status = AccessTokenStatus.EmptyOrUndefined;
			} else {
				accessTokenInfo = AccessTokenInfo.parse(iAccessTokenInfo);
			}
			Vue.$accessTokenInfo = accessTokenInfo;
			return accessTokenInfo;
		} catch (e) {
			console.error(e);
			const accessTokenInfo: AccessTokenInfo = new AccessTokenInfo();
			Vue.$accessTokenInfo = accessTokenInfo;
			return accessTokenInfo;
		}
	}

	/**
	 * アクセストークン情報をグローバル変数およびローカルストレージから削除する。
	 */
	public static removeAccessTokenInfo(): void {
		try {
			const accessTokenInfo: AccessTokenInfo = new AccessTokenInfo();
			accessTokenInfo.status = AccessTokenStatus.EmptyOrUndefined;
			Vue.$accessTokenInfo = accessTokenInfo;
			StoreUtils.removeLocalStorage(Consts.LOCAL_STORAGE_KEY_MSAL_ACCESSTOKENINFO);
		} catch (e) {
			console.error(e);
		}
	}
}
